import * as Sentry from '@sentry/react';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18next from 'i18next';
// eslint-disable-next-line
import packageJson from '../../../../../package.json';
import { Result } from '../../../types/response';
import store from '../../store';
import getBrowserFingerPrint from '../fingerprintUtils';
import { APIError, captureInterfaceError, catchError, isError, regularErrorCodes } from '../index';
import { nativeApi } from '../nativeApi';
import { Platform } from '../nativeApi/interface';
import { SentryErrorType } from '../sentry/capture';

// const STARTSENTRY = String(import.meta.env.VITE_APP_STARTSENTRY) === 'true';
const STARTSENTRY = true;

const source = axios.CancelToken.source();

// 获取当前可用的用户 token：按 Redux -> nativeApi -> localStorage('userToken') 的优先级获取
const getEffectiveUserToken = async (): Promise<string> => {
  const storeToken = store.getState().commonReducer.userToken;
  if (storeToken) return storeToken;

  const nativeToken = await nativeApi.getToken?.();
  if (nativeToken) return nativeToken as string;

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const localToken = window.localStorage.getItem('userToken');
      if (localToken) return localToken;
    }
  } catch (e) {
    // 读 localStorage 失败时静默降级
  }

  return '';
};

const u = navigator.userAgent;
const isiOS = !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/);
const requestPrefix = isiOS ? 'IH' : 'AH';

const getRandomNum = () => String(Math.random()).slice(-6);

const getProductCode = () => (location.pathname.includes('clCredit') ? 'CL' : 'KKC');

const handleConfigs = async (config: any) => {
  const { productCode, language } = config;

  // 统一从 Redux、nativeApi 与 localStorage 中获取最终可用的 token
  const awaitToken = await getEffectiveUserToken();

  const environment = String(import.meta.env.VITE_APP_ENV || '')
    .trim()
    .toLowerCase();
  const canUseDevToken = ['development', 'staging', 'uat'].includes(environment);
  let customToken = '';
  if (canUseDevToken) {
    try {
      customToken = sessionStorage.getItem('devToken')?.trim() || '';
    } catch (_error) {
      customToken = '';
    }
  }
  const authorizationToken = (customToken || awaitToken).replace(/^Bearer\s+/i, '').trim();
  const authorizationHeader = {
    Authorization: authorizationToken ? `Bearer ${authorizationToken}` : '',
  };
  const requestID = `${requestPrefix}${new Date().getTime()}${getRandomNum()}`;
  const nativeHeaders = nativeApi.getRequestHeader?.();
  config.headers = {
    ...config.headers,
    ...{
      version: packageJson.version,
      appNameCode: productCode || getProductCode(),
      language: language || i18next.language,

      // login 接口换取来的 token：优先使用 Redux 中的 userToken，
      // 否则回退到 native 层提供的 token，避免出现「令牌不能为空」的问题。
      token: awaitToken || '',

      requestID,
      vcreditTraceId: requestID,
      client: nativeApi.name,
      sourceChannel:
        nativeApi.name === Platform.MyLink
          ? 'MyLink'
          : nativeApi.name === Platform.Crefit
          ? 'CreFIT'
          : 'H5',
      browserID: await getBrowserFingerPrint(),
      deviceId: (await nativeApi.getDeviceId?.()) || '',
      hardwareId: (await nativeApi.getHardwareId?.()) || '',
    },
    ...nativeHeaders,
    ...authorizationHeader,
  };

  // byClaude-start 2026-07-23 13:59:33
  // 文件上传使用 FormData 时，跳过 JSON 包裹并移除 Content-Type 让浏览器自动设置 multipart boundary
  if (config.method === 'post' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
    return config;
  }
  // byClaude-end
  if (config.method === 'post') {
    config.data instanceof Array
      ? config.data
      : (config.data = {
          // 如果 productCode 为 null，则不添加 productCode 到请求体
          ...(productCode !== null ? { productCode: productCode || getProductCode() } : {}),
          ...config.data,
        });
  }
  const curToken = config.headers?.Authorization || '';
  if (
    (config?.url?.includes('clPay/executeFlow') ||
      config?.url?.includes('pay/sureLoan') ||
      config?.url?.includes('cmcPay/executeFlow') ||
      config?.url?.includes('clPay/h5/executeFlow')) &&
    curToken
  ) {
    const interestCategoryMap = JSON.parse(sessionStorage.getItem('interestCategoryMap') || '{}');
    const interestCategory = interestCategoryMap[curToken] || '';
    if (interestCategory) {
      config.data = {
        ...config.data,
        interestCategory,
      };
    }
  }
  return config;
};

export const createAxiosHttp = (
  _config: AxiosRequestConfig = {},
  baseURL = import.meta.env.VITE_APP_BASE_API
): AxiosInstance => {
  const axiosParams: AxiosRequestConfig = {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json;charset=utf-8',
    },
    timeout: 30000,
    baseURL,
    ..._config,
  };
  const request = axios.create(axiosParams);
  request.interceptors.request.use(
    async (config: any) => {
      config.baseURL = baseURL;
      config = handleConfigs(config);
      if (STARTSENTRY) {
        const startTime = new Date();
        // console.log('🚀 ~ startTime: ====== ', startTime);
        config.transaction = Sentry?.startInactiveSpan({
          op: 'http.client',
          name: `${config.method} ${config.url}`,
          forceTransaction: true,
        });
        config.startTime = startTime;
      }
      return config;
    },
    (error) => {
      console.log('出现了问题');
      Promise.reject(error);
    }
  );

  request.interceptors.response.use(
    (response: AxiosResponse<Result>) => {
      const { data, status, config } = response;
      const { msg, code } = data || '';
      const { data: res } = data || '';
      const message = `接口${config.url}报错: ${msg}`;
      // 添加一个接口返回数据的埋点
      if (code === 200) {
        const uploadData = {
          url: config.url,
          requestId: config.headers?.requestID,
          reqdata: JSON.stringify(config.data),
          resdata: JSON.stringify(res),
        };
        sensors.track('Web_Api_Response', uploadData);
        console.log('Web_Api_Response', uploadData);
        if (
          config?.url?.includes('/repaymentPlan') &&
          !config?.url?.includes('/repaymentPlanByOrderId')
        ) {
          const curToken = config?.headers?.Authorization || '';
          if (res?.interestCategory && curToken) {
            const interestCategoryMap = JSON.parse(
              sessionStorage.getItem('interestCategoryMap') || '{}'
            );
            interestCategoryMap[String(curToken)] = res?.interestCategory;
            sessionStorage.setItem('interestCategoryMap', JSON.stringify(interestCategoryMap));
          }
        }
      } else {
        sensors.track('Web_Api_Error', {
          url: config.url,
          requestId: config.headers?.requestID,
          errorData: JSON.stringify(data),
        });
        console.warn('Web_Api_Error', message);
      }
      if (STARTSENTRY && code !== 200 && !regularErrorCodes.includes(code)) {
        const { startTime, span } = (response.config as any) || {};
        const apiTime = startTime ? new Date().getTime() - startTime.getTime() : 0;
        span?.setAttribute('http.status_code', status);
        span?.setAttribute('http.response_data', JSON.stringify(data));
        span?.finish();
        captureInterfaceError(response, {
          apiTime,
          message,
          errorLevel: 'warn',
        });
      }
      source.cancel();

      if (status === 200 && code === 200) {
        Promise.resolve(res);
        return res;
      }
      return Promise.reject(new APIError(msg, code));
    },
    (error) => {
      if (STARTSENTRY) {
        if (isError(error)) {
          catchError({
            errorType: SentryErrorType.InterfaceError,
            errorObj: error,
            errorLevel: 'error',
          });
        } else if (error.config) {
          const { startTime } = error.config as any;
          const res = error.response;
          const apiTime = startTime ? new Date().getTime() - startTime.getTime() : 0;
          const message = `接口${res.config.url}报错： ${res.msg}`;
          console.log(message, 'message');
          captureInterfaceError(res, {
            apiTime,
            message,
            errorLevel: 'error',
          });
        }
        if (error?.config?.span) {
          error.config.span.finish();
        }
      }
      return Promise.reject(error);
    }
  );
  return request;
};

export default createAxiosHttp();
